/**
 * AuthenticatorAltActions — the footer links under the push-2FA screen.
 *
 * Extracted from `<AuthenticatorChallengeFlow>` (Q-D-TOTP-VERIFY, 2026-08-29): three sibling
 * conditionals in one footer took that component past `web/render-method-complexity`'s ceiling.
 * GEN0-STRUCTURAL-ELIMINATION forbids suppressing a structural rule, so the footer becomes a
 * component — which also gives the "is there anywhere else to go from here" decision one home.
 *
 * @module @puffin-core/login-shell/components/AuthenticatorAltActions
 */

import PropTypes from 'prop-types';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';

/**
 * @param {Object} props
 * @param {boolean} props.showUseCode - Whether a fallback exists AND the delay has elapsed.
 * @param {Function} props.onUseCode
 * @param {boolean} props.codeMode - True while the code form is showing.
 * @param {Function} props.onBackToPush
 * @param {Function} [props.onCancel]
 * @param {Function} [props.t]
 * @returns {React.JSX.Element}
 */
export function AuthenticatorAltActions({
  showUseCode,
  onUseCode,
  codeMode,
  onBackToPush,
  onCancel,
  t,
}) {
  const translate = useLoginTranslate(t);

  return (
    <div className="mfa-alt-actions">
      {showUseCode && (
        <button
          type="button"
          className="mfa-alt-link"
          onClick={onUseCode}
          data-testid="authenticator-use-code-btn"
        >
          {translate('authenticator.useCodeInstead')}
        </button>
      )}
      {codeMode && (
        <button
          type="button"
          className="mfa-alt-link"
          onClick={onBackToPush}
          data-testid="authenticator-back-to-push-btn"
        >
          {translate('authenticator.backToPush')}
        </button>
      )}
      {onCancel && (
        <button
          type="button"
          className="mfa-alt-link"
          onClick={onCancel}
          data-testid="authenticator-cancel-btn"
        >
          {translate('authenticator.cancel')}
        </button>
      )}
    </div>
  );
}

AuthenticatorAltActions.propTypes = {
  showUseCode: PropTypes.bool.isRequired,
  onUseCode: PropTypes.func.isRequired,
  codeMode: PropTypes.bool.isRequired,
  onBackToPush: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  t: PropTypes.func,
};
